// Ticket Detail Page - View and respond to tickets
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Stack,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  CircularProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  MoreVert,
  Person,
  Computer,
  AttachFile,
  Image,
  Note,
  Terminal,
  ExpandMore,
  ExpandLess,
  Delete,
  Close,
  ZoomIn,
} from '@mui/icons-material';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/contexts/ToastContext';
import { ticketsService } from '@/services/collections/tickets';
import { ticketResponsesService } from '@/services/collections/ticketResponses';
import { organizationsService } from '@/services/collections/organizations';
import { notificationsService } from '@/services/collections/notifications';
import { storageService } from '@/services/firebase/storage';
import { formatRelativeTime } from '@/services/firebase/firestore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { Ticket, TicketResponse, TicketStatus, TicketPriority, OrganizationMember, Attachment } from '@/types';

const STATUS_OPTIONS: { value: TicketStatus; label: string; color: any }[] = [
  { value: 'new', label: 'New', color: 'info' },
  { value: 'open', label: 'Open', color: 'warning' },
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
  { value: 'closed', label: 'Closed', color: 'default' },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: any }[] = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'medium', label: 'Medium', color: 'info' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'urgent', label: 'Urgent', color: 'error' },
];

export const TicketDetailPage = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { organization, project } = useOrganization();
  const { user, profile } = useAuth();
  const { canManageTickets, canAssignTickets } = usePermissions();
  const { showSuccess, showError } = useToast();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [replyContent, setReplyContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Attachment and preview state
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [showConsoleErrors, setShowConsoleErrors] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch ticket and subscribe to responses
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchTicket = async () => {
      if (!ticketId) return;

      setLoading(true);
      try {
        const data = await ticketsService.getById(ticketId);

        // Verify ticket belongs to current organization
        if (data && organization && data.organizationId !== organization.id) {
          showError('Ticket not found');
          navigate('/app/tickets');
          return;
        }

        setTicket(data);

        // Fetch team members for assignment
        if (organization) {
          const memberList = await organizationsService.getMembers(organization.id);
          setMembers(memberList.filter((m) => m.status === 'active'));
        }

        // Subscribe to responses
        unsubscribe = ticketResponsesService.subscribe(
          ticketId,
          (newResponses) => setResponses(newResponses),
          (error) => console.error('Error subscribing to responses:', error)
        );
      } catch (error) {
        console.error('Error fetching ticket:', error);
        showError('Failed to load ticket');
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [ticketId, organization]);

  // Update ticket status
  const handleStatusChange = async (status: TicketStatus) => {
    if (!ticket) return;

    try {
      await ticketsService.updateStatus(ticket.id, status);
      setTicket({ ...ticket, status });
      showSuccess('Status updated');
    } catch (error) {
      showError('Failed to update status');
    }
  };

  // Update ticket priority
  const handlePriorityChange = async (priority: TicketPriority) => {
    if (!ticket) return;

    try {
      await ticketsService.updatePriority(ticket.id, priority);
      setTicket({ ...ticket, priority });
      showSuccess('Priority updated');
    } catch (error) {
      showError('Failed to update priority');
    }
  };

  // Assign ticket
  const handleAssign = async (memberId: string, memberName: string) => {
    if (!ticket) return;

    try {
      await ticketsService.assign(ticket.id, memberId, memberName);
      setTicket({ ...ticket, assigneeId: memberId, assigneeName: memberName, status: 'open' });
      showSuccess(`Assigned to ${memberName}`);
      setAssignDialog(false);
    } catch (error) {
      showError('Failed to assign ticket');
    }
  };

  // Handle file selection for attachments
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB max per file

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        showError(`${file.name} is too large (max 10MB)`);
      } else {
        validFiles.push(file);
      }
    });

    setReplyAttachments((prev) => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment from reply
  const handleRemoveAttachment = (index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Send reply
  const handleSendReply = async () => {
    if (!ticket || !replyContent.trim() || !user || !organization || !project) return;

    setSending(true);
    setUploadingAttachments(replyAttachments.length > 0);

    try {
      // Upload attachments if any
      let attachments: Attachment[] = [];
      if (replyAttachments.length > 0) {
        attachments = await storageService.uploadAttachments(
          replyAttachments,
          `organizations/${organization.id}/tickets/${ticket.id}/responses`
        );
      }

      // Create the response with attachments
      await ticketResponsesService.createAgentResponse(
        ticket.id,
        organization.id,
        project.id,
        replyContent.trim(),
        user.uid,
        profile?.displayName || user.email || 'Agent',
        user.email || '',
        isInternal,
        attachments
      );

      showSuccess(isInternal ? 'Internal note added' : 'Reply sent');
      setReplyContent('');
      setReplyAttachments([]);

      // Queue email notification to customer (non-internal replies only)
      if (!isInternal && ticket.submitterEmail && organization) {
        try {
          await notificationsService.notifyCustomerOfReply(
            ticket,
            {
              id: `reply_${Date.now()}`,
              content: replyContent.trim(),
              authorId: user.uid,
              authorName: profile?.displayName || user.email || 'Agent',
              authorEmail: user.email || '',
              isInternal: false,
            } as TicketResponse,
            organization,
            window.location.origin
          );
        } catch (notifErr) {
          console.error('Failed to queue notification:', notifErr);
        }
      }

      // If first response, update firstResponseAt and status
      if (!ticket.firstResponseAt && !isInternal) {
        await ticketsService.update(ticket.id, {
          firstResponseAt: new Date() as any,
          status: 'open',
        });
        setTicket({ ...ticket, firstResponseAt: new Date() as any, status: 'open' });
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      showError('Failed to send reply');
    } finally {
      setSending(false);
      setUploadingAttachments(false);
    }
  };

  // Close ticket
  const handleCloseTicket = async () => {
    if (!ticket) return;

    try {
      await ticketsService.close(ticket.id);
      setTicket({ ...ticket, status: 'closed' });
      showSuccess('Ticket closed');
      setMenuAnchor(null);

      // Notify customer of resolution
      if (ticket.submitterEmail && organization) {
        try {
          await notificationsService.notifyCustomerOfResolution(ticket, organization, window.location.origin);
        } catch (notifErr) {
          console.error('Failed to queue resolution notification:', notifErr);
        }
      }
    } catch (error) {
      showError('Failed to close ticket');
    }
  };

  // Delete ticket
  const handleDeleteTicket = async () => {
    if (!ticket) return;

    setDeleting(true);
    try {
      await ticketsService.delete(ticket.id);
      showSuccess('Ticket deleted');
      navigate('/app/tickets');
    } catch (error) {
      showError('Failed to delete ticket');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage message="Loading ticket..." />;
  }

  if (!ticket) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Ticket not found
        </Typography>
        <Button onClick={() => navigate('/app/tickets')} sx={{ mt: 2 }}>
          Back to Tickets
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/app/tickets')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            #{ticket.ticketNumber || ticket.id.slice(0, 6)}: {ticket.subject}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Submitted by {ticket.submitterEmail} · {formatRelativeTime(ticket.createdAt)}
          </Typography>
        </Box>
        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVert />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={handleCloseTicket}>Close Ticket</MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); setDeleteDialogOpen(true); }} sx={{ color: 'error.main' }}>Delete Ticket</MenuItem>
        </Menu>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Ticket Content */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {ticket.description}
              </Typography>

              {/* Screenshots */}
              {ticket.screenshots?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <Image sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Screenshots ({ticket.screenshots.length})
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {ticket.screenshots.map((url, i) => (
                      <Box
                        key={i}
                        sx={{ position: 'relative' }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={`Screenshot ${i + 1}`}
                          sx={{
                            width: 120,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: 1,
                            borderColor: 'divider',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.02)',
                            },
                          }}
                          onClick={() => setScreenshotPreview(url)}
                        />
                        <Tooltip title="View full size">
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                              width: 24,
                              height: 24,
                            }}
                            onClick={() => setScreenshotPreview(url)}
                          >
                            <ZoomIn sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Attachments */}
              {ticket.attachments?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <AttachFile sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Attachments ({ticket.attachments.length})
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {ticket.attachments.map((file, i) => (
                      <Chip
                        key={i}
                        label={file.name}
                        size="small"
                        onClick={() => window.open(file.url, '_blank')}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Console Errors */}
              {ticket.consoleErrors && ticket.consoleErrors.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowConsoleErrors(!showConsoleErrors)}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      <Terminal sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Console Errors ({ticket.consoleErrors.length})
                    </Typography>
                    <IconButton size="small">
                      {showConsoleErrors ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                  <Collapse in={showConsoleErrors}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {ticket.consoleErrors.map((error, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            color: '#f87171',
                            mb: 1,
                            pb: 1,
                            borderBottom: idx < ticket.consoleErrors!.length - 1 ? '1px solid #333' : 'none',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {error}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Conversation Thread */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Conversation ({responses.length})
                </Typography>
                {organization?.slug && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const replyUrl = `${window.location.origin}/p/${organization.slug}/ticket/${ticket.id}`;
                      navigator.clipboard.writeText(replyUrl);
                      showSuccess('Reply link copied! Share with customer.');
                    }}
                  >
                    Copy Reply Link
                  </Button>
                )}
              </Box>

              {responses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No replies yet. Be the first to respond!
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {responses.map((response) => (
                    <Paper
                      key={response.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: response.isInternal
                          ? 'warning.50'
                          : response.isAgent
                            ? 'primary.50'
                            : 'grey.50',
                        borderLeft: 3,
                        borderColor: response.isInternal
                          ? 'warning.main'
                          : response.isAgent
                            ? 'primary.main'
                            : 'grey.400',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                          {response.authorName[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">
                            {response.authorName}
                            {response.isInternal && (
                              <Chip
                                label="Internal"
                                size="small"
                                color="warning"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                            {!response.isAgent && (
                              <Chip
                                label="Customer"
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(response.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pl: 4.5 }}>
                        {response.content}
                      </Typography>

                      {/* Response Attachments */}
                      {response.attachments && response.attachments.length > 0 && (
                        <Box sx={{ pl: 4.5, mt: 1.5 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {response.attachments.map((file, i) => {
                              const isImage = file.type?.startsWith('image/');
                              return isImage ? (
                                <Tooltip key={i} title={file.name}>
                                  <Box
                                    component="img"
                                    src={file.url}
                                    alt={file.name}
                                    sx={{
                                      width: 60,
                                      height: 45,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      border: 1,
                                      borderColor: 'divider',
                                    }}
                                    onClick={() => window.open(file.url, '_blank')}
                                  />
                                </Tooltip>
                              ) : (
                                <Chip
                                  key={i}
                                  label={file.name}
                                  size="small"
                                  icon={<AttachFile fontSize="small" />}
                                  onClick={() => window.open(file.url, '_blank')}
                                  sx={{ cursor: 'pointer' }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Reply Box */}
          {canManageTickets && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant={!isInternal ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setIsInternal(false)}
                  >
                    Reply
                  </Button>
                  <Button
                    variant={isInternal ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<Note />}
                    onClick={() => setIsInternal(true)}
                  >
                    Internal Note
                  </Button>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={isInternal ? 'Add an internal note...' : 'Type your reply...'}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Selected Attachments Preview */}
                {replyAttachments.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Attachments ({replyAttachments.length})
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {replyAttachments.map((file, index) => (
                        <Chip
                          key={index}
                          label={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                          size="small"
                          onDelete={() => handleRemoveAttachment(index)}
                          deleteIcon={<Delete fontSize="small" />}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      hidden
                      accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json"
                    />
                    <Tooltip title="Attach files (max 10MB each)">
                      <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                        <Badge badgeContent={replyAttachments.length} color="primary">
                          <AttachFile />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={sending ? (uploadingAttachments ? <CircularProgress size={16} /> : <CircularProgress size={16} />) : <Send />}
                    onClick={handleSendReply}
                    disabled={!replyContent.trim() || sending}
                  >
                    {sending
                      ? (uploadingAttachments ? 'Uploading...' : 'Sending...')
                      : (isInternal ? 'Add Note' : 'Send Reply')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Status & Priority */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                  disabled={!canManageTickets}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Chip label={opt.label} size="small" color={opt.color} sx={{ mr: 1 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Priority
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={ticket.priority}
                  onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
                  disabled={!canManageTickets}
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Chip label={opt.label} size="small" color={opt.color} sx={{ mr: 1 }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Assignee
              </Typography>
              {ticket.assigneeName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32 }}>{ticket.assigneeName[0]}</Avatar>
                  <Typography>{ticket.assigneeName}</Typography>
                </Box>
              ) : (
                <Typography color="text.secondary">Unassigned</Typography>
              )}
              {canAssignTickets && (
                <Button size="small" onClick={() => setAssignDialog(true)} sx={{ mt: 1 }}>
                  {ticket.assigneeName ? 'Reassign' : 'Assign'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Submitter Info */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Submitter
              </Typography>
              <Typography variant="body2">{ticket.submitterName || 'Unknown'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ticket.submitterEmail}
              </Typography>
            </CardContent>
          </Card>

          {/* Session Context */}
          {ticket.sessionContext && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <Computer sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Session Info
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="caption">
                    <strong>URL:</strong> {ticket.sessionContext.url}
                  </Typography>
                  <Typography variant="caption">
                    <strong>Browser:</strong> {ticket.sessionContext.browser}
                  </Typography>
                  <Typography variant="caption">
                    <strong>OS:</strong> {ticket.sessionContext.os}
                  </Typography>
                  <Typography variant="caption">
                    <strong>Viewport:</strong> {ticket.sessionContext.viewport}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {ticket.tags?.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tags
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {ticket.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {members.map((member) => (
              <Paper
                key={member.userId}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => handleAssign(member.userId, member.displayName)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32 }}>{member.displayName[0]}</Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {member.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {member.role}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Screenshot Preview Dialog */}
      <Dialog
        open={Boolean(screenshotPreview)}
        onClose={() => setScreenshotPreview(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Screenshot Preview</Typography>
          <IconButton onClick={() => setScreenshotPreview(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#1e1e1e' }}>
          {screenshotPreview && (
            <Box
              component="img"
              src={screenshotPreview}
              alt="Screenshot preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(screenshotPreview!, '_blank')}>
            Open in New Tab
          </Button>
          <Button onClick={() => setScreenshotPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete ticket #{ticket?.ticketNumber || ticket?.id.slice(0, 6)}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteTicket} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
